/**
 * @lines98/client/lib/meta-snapshot — оптимистичный кэш мета-состояния игрока для
 * МГНОВЕННОГО показа динамики Мастерской/детали зверя до ресолва `GET /meta/state`.
 *
 * ЗАЧЕМ / WHY THIS EXISTS (owner-playtest fix 2026-06-10, прецедент `lib/daily-final-state`):
 * структура Мастерской рисуется сразу (глифы зверей + пустые контуры), но ВСЯ ДИНАМИКА —
 * вставленные камни, уровень зверя, карат-престиж, бонус, стоимость улучшения, 7 счётчиков
 * пула + золото/осколки — приходит только ПОСЛЕ async `getMetaState()`, отчего при входе и
 * перезагрузке числа «передёргиваются» (плейсхолдеры/«—»/0 → значения). Этот модуль —
 * ЕДИНЫЙ переиспользуемый снимок+кэш (по образцу done-доски дня): после каждого успешного
 * `getMetaState` мы кладём снимок динамики, а при следующем входе рисуем его МГНОВЕННО,
 * затем reconcile server-truth (поправляет, если разошлось; совпало — без скачка).
 *
 * **Server-truth НЕ нарушается (I-G6 / AC-G11):** кэш — ТОЛЬКО для мгновенного показа.
 * Истина — всегда следующий `getMetaState`; reconcile перетирает кэш серверным значением.
 * Мутации (вставка/сплав/крафт/апгрейд) шлют intent, НЕ баланс — кэш не участвует в
 * серверных проверках (анти-чит цел; кэш — UX-проекция, как daily-cache «boundary»).
 *
 * **Ключ по идентичности игрока (privacy):** снимок хранится под `playerId` (см.
 * `services/identity`). Аккаунт A не видит кэш аккаунта B; гость (нет identity) → пусто.
 * При смене игрока (логин в другой аккаунт) старый снимок не читается (ключ другой).
 *
 * **Backend: localStorage (синхронный).** В отличие от daily-cache (IDB-first, async),
 * снимок читается СИНХРОННО при mount — чтобы отрисовать динамику в тот же тик, ДО любого
 * сетевого/IDB-await. Снимок мал (балансы + 7 чисел + ≤5 зверей), localStorage подходит.
 * Все операции defensive (catch+ignore): сбой кэша деградирует UX мягко, не ломает экран
 * и НИКОГДА не блокирует server-truth.
 *
 * **Тонкая ветка над `lib/screen-snapshot` (рефактор 2026-06-10):** persistence /
 * изоляция по playerId / версионирование / синхронный read — в общем «стволе»
 * `createScreenSnapshot<T>`. Здесь — ТОЛЬКО мета-специфика: форма `MetaSnapshot`,
 * проекция из `MetaStateSuccess`, type-guard. Тот же движок переиспользует лидерборд
 * (модель «ствол → ветки», code-structure §1) — без дубля кэш-логики.
 *
 * Cross-references:
 *   - lib/screen-snapshot.ts (ствол: per-player snapshot-движок — load/save/clear).
 *   - lib/daily-final-state.ts (прецедент: снимок при финише + ресинк server-truth).
 *   - services/daily-cache.ts (UX-only boundary, schema-version guard, defensive storage).
 *   - services/meta-api.ts (MetaStateSuccess — источник снимка; getMetaState — истина).
 *   - screens/workshop-screen.ts (consumer: hydrate-from-cache → reconcile).
 */

import { createScreenSnapshot } from './screen-snapshot.js';
import type {
  GnomeStateWire,
  MetaStateSuccess,
  ProfileStats,
} from '../services/meta-api.js';

// =============================================================================
// Types
// =============================================================================

/**
 * Кэшируемая ПРОГРЕССИЯ аккаунта — ровно поля, что рисует Профиль (уровень/XP-прогресс/
 * id-ачивок/слоты). Подмножество `ProgressionState` (server-truth): id-ачивок храним
 * плоским списком (для `badge-grid` нужны только id), числители/знаменатели бара — как
 * есть (клиент НЕ считает уровень, рисует server-truth доли). Это та же мета-проекция —
 * НЕ второй кэш: Профиль и Мастерская читают ОДИН снимок (workshop рисует jewelry/пул,
 * profile — прогрессию; оба — из `getMetaState`).
 */
export interface MetaSnapshotProgression {
  /** Текущий уровень (бейдж). */
  readonly level: number;
  /** XP внутри уровня (числитель прогресс-бара). */
  readonly xpIntoLevel: number;
  /** XP всего уровня (знаменатель прогресс-бара). */
  readonly xpForLevel: number;
  /** XP до следующего уровня (подпись). */
  readonly xpToNext: number;
  /** Id выданных ачивок (для `badge-grid` — открытые ячейки). */
  readonly achievementIds: readonly string[];
  /** Заработанные слоты Книги (от вех уровня). */
  readonly earnedSlots: number;
}

// MetaSnapshotJewelry (динамика зверя) УДАЛЁН вместе с «животными»
// (workshop-animals-removal.md): мастерская/карточка зверя сняты, снимок их не кэширует.

/**
 * Кэшируемый слот гнома (GN-2b) — `color`/`unlocked`/`level`/`prestigeCount`. Подмножество
 * `GnomeStateWire` (server-truth, §4.1): для locked-каркаса мгновенно нужны только цвет +
 * открытость; открытый несёт level/prestige для карточки. Снимок хранит то, что прислал
 * сервер (обычно открытые слоты — экран достроит до 7 locked'ами, AC-12).
 */
export type MetaSnapshotGnome = Pick<
  GnomeStateWire,
  'color' | 'unlocked' | 'level' | 'prestigeCount'
>;

/**
 * Снимок мета-динамики игрока для мгновенного рендера. Содержит всё, что иначе
 * «передёргивается» при входе/перезагрузке: балансы, пул камней, динамика зверей,
 * карат + прогрессия (для Профиля) + гномы (GN-2b — locked-каркас рисуется мгновенно).
 * `v` — версия схемы (несовпадение → снимок битый, отбрасываем). Один снимок
 * переиспользуют мета-экраны (Мастерская / Профиль / Гномы).
 */
export interface MetaSnapshot {
  readonly v: 6;
  /** Золото — мягкая валюта (HUD-счётчик). */
  readonly gold: number;
  /** Осколки — расходник (HUD-счётчик). */
  readonly shards: number;
  /** Вечный карат (HUD Профиля + статистика/галерея). */
  readonly karat: number;
  /** Пул камней (массив 1..7; индекс 0 sentinel; длина 8) — 7 счётчиков HUD пула. */
  readonly gemPool: readonly number[];
  // jewelry[] (динамика зверей) удалён вместе с «животными» (workshop-animals-removal.md).
  /** Прогрессия аккаунта (уровень/XP/ачивки/слоты) — секции Профиля. */
  readonly progression: MetaSnapshotProgression;
  /**
   * Игровая статистика профиля (DEP-1..4 — партий / лучший счёт по режимам / стрик /
   * ранг доски дня). Дашборд-Профиль гидрирует KPI-плитки из снимка СИНХРОННО при
   * mount (AC-10 instant-hydration: значения не «прыгают» 0→число), затем reconcile
   * server-truth. Включён в снимок v3 (раньше Профиль был jeweler-эры без KPI).
   */
  readonly profileStats: ProfileStats;
  /**
   * Слоты гномов (GN-2b) — обычно открытые (экран достроит locked'ами до 7, AC-12).
   * Locked-каркас плашки «Гномы» рисуется из них СИНХРОННО при mount (instant-hydration,
   * AC-9) — не «вщёлкивание» открытости.
   */
  readonly gnomes: readonly MetaSnapshotGnome[];
  /** Сколько вех открытия выдано (0..7) — для определения прогресса/состояния. */
  readonly gnomeUnlocksGranted: number;
  /**
   * Уровень следующей вехи (server-truth) или null если все 7 открыты — строка
   * «следующий гном на уровне N» рисуется из снимка сразу (NO-HARDCODE, AC-3).
   */
  readonly nextUnlockLevel: number | null;
  /** ms epoch снимка (для возможной TTL-логики / отладки; рендер от него не зависит). */
  readonly timestamp: number;
}

// =============================================================================
// Constants + общий снимок-«ствол» (per-player store)
// =============================================================================

/** Префикс ключа; полный ключ = `${PREFIX}${playerId}` (изоляция по игроку). */
const KEY_PREFIX = 'lines98.meta.snapshot.v1.';
/**
 * Версия схемы payload `MetaSnapshot` (несовпадение → снимок битый, отбрасываем).
 * v2: добавлены `karat` + `progression` (Профиль читает тот же снимок).
 * v3: добавлен `profileStats` (DEP-1..4 — дашборд-Профиль гидрирует KPI из снимка
 *     синхронно, AC-10).
 * v4: добавлен блок `gnomes` + `gnomeUnlocksGranted` + `nextUnlockLevel` (GN-2b —
 *     locked-каркас плашки «Гномы» гидрируется синхронно, AC-9). Старый v1/v2/v3-снимок
 *     (без блока) отбрасывается gracefully — экран дотянет server-truth (skeleton → fade).
 * v5: добавлен `profileStats.modeStats` (per-mode таблица Профиля, ADR-0019 —
 *     таблица режимов гидрируется синхронно). Старый v1..v4-снимок отбрасывается.
 * v6: удалён блок `jewelry[]` вместе с «животными» (workshop-animals-removal.md) —
 *     старый v1..v5-снимок (со зверями в localStorage) отбрасывается gracefully (AC-8:
 *     гидрация нового клиента не ломается; гномы/балансы дотянет server-truth).
 */
const SCHEMA_VERSION = 6;

/**
 * Per-player snapshot-store для меты — поверх общего «ствола» `createScreenSnapshot`.
 * Persistence / изоляция по playerId / версионирование / синхронный read живут там;
 * здесь — только мета-тип `MetaSnapshot` + его type-guard.
 */
const store = createScreenSnapshot<MetaSnapshot>({
  keyPrefix: KEY_PREFIX,
  version: SCHEMA_VERSION,
  isValid: isMetaSnapshot,
});

// =============================================================================
// Build (из server-truth)
// =============================================================================

/**
 * Собрать снимок из server-truth `MetaStateSuccess` (после успешного `getMetaState`).
 * Копирует только видимую динамику (балансы + пул + per-card поля), глубоко копируя
 * `gemPool`, чтобы снимок не мутировал вслед за источником.
 *
 * @param state — успешный ответ `getMetaState` (server-truth).
 * @param now — ms epoch (инжектируемые часы; по умолчанию `Date.now()`).
 */
export function buildMetaSnapshot(state: MetaStateSuccess, now: number = Date.now()): MetaSnapshot {
  const p = state.progression;
  // Defensive: на `MetaStateSuccess` из getMetaState блок всегда есть (toProfileStats
  // дефолтит), но фикстуры/старые вызовы могут его опустить → safe-дефолт (без краша).
  const s: ProfileStats = state.profileStats ?? {
    gamesTotal: 0,
    dailyCount: 0,
    modesEverPlayed: 0,
    bestByMode: {},
    currentDailyStreak: 0,
    dailyRank: null,
    modeStats: {},
  };
  return {
    v: 6,
    gold: state.gold,
    shards: state.shards,
    karat: state.karat,
    gemPool: [...state.gemPool],
    progression: {
      level: p.level,
      xpIntoLevel: p.xpIntoLevel,
      xpForLevel: p.xpForLevel,
      xpToNext: p.xpToNext,
      achievementIds: p.achievements.map((a) => a.achievementId),
      earnedSlots: p.earnedSlots,
    },
    // KPI-статистика — глубокая копия `bestByMode` + `modeStats`, чтобы снимок не
    // мутировал вслед за источником (forward-compat дефолты пробрасываются как есть).
    profileStats: {
      gamesTotal: s.gamesTotal,
      dailyCount: s.dailyCount,
      modesEverPlayed: s.modesEverPlayed,
      bestByMode: { ...s.bestByMode },
      currentDailyStreak: s.currentDailyStreak,
      dailyRank: s.dailyRank,
      // Per-mode таблица (ADR-0019) — мелкая копия записей (значения примитивны).
      modeStats: { ...s.modeStats },
    },
    // Гномы (GN-2b) — копируем server-truth слоты (обычно открытые; экран достроит
    // locked'ами до 7, AC-12). Locked-каркас рисуется из них мгновенно (AC-9). Defensive:
    // на `MetaStateSuccess` из getMetaState блок всегда есть (toGnomes дефолтит к []), но
    // старые фикстуры/вызовы могут его опустить → safe-дефолт (без краша, как profileStats).
    gnomes: (state.gnomes ?? []).map((g) => ({
      color: g.color,
      unlocked: g.unlocked,
      level: g.level,
      prestigeCount: g.prestigeCount,
    })),
    gnomeUnlocksGranted: state.gnomeUnlocksGranted ?? 0,
    nextUnlockLevel: state.nextUnlockLevel ?? null,
    timestamp: now,
  };
}

// toSnapshotJewelry удалён вместе с «животными» (workshop-animals-removal.md).

// =============================================================================
// Persistence — тонкие обёртки над общим store (имена сохранены для consumer'ов)
// =============================================================================

/**
 * Прочитать снимок ТЕКУЩЕГО игрока СИНХРОННО (для мгновенного рендера при mount).
 * `null`, если: гость (нет identity), снимка нет, форма/версия не совпали (битый),
 * либо localStorage недоступен. Чистый read — server-truth остаётся истиной.
 */
export function loadMetaSnapshot(): MetaSnapshot | null {
  return store.load();
}

/**
 * Сохранить снимок текущего игрока (после успешного `getMetaState` / ресинка мутации,
 * чтобы кэш не устаревал грубо). No-op для гостя (нет ключа). Defensive: сбой записи
 * (quota / приватный режим) проглатывается — это UX-кэш, не истина.
 */
export function saveMetaSnapshot(snapshot: MetaSnapshot): void {
  store.save(snapshot);
}

/** Удалить снимок текущего игрока (явный сброс / тесты). */
export function clearMetaSnapshot(): void {
  store.clear();
}

// =============================================================================
// Type guard
// =============================================================================

// isSnapshotJewelry удалён вместе с «животными» (workshop-animals-removal.md).

function isSnapshotProgression(value: unknown): value is MetaSnapshotProgression {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v['level'] === 'number' &&
    typeof v['xpIntoLevel'] === 'number' &&
    typeof v['xpForLevel'] === 'number' &&
    typeof v['xpToNext'] === 'number' &&
    Array.isArray(v['achievementIds']) &&
    (v['achievementIds'] as unknown[]).every((s) => typeof s === 'string') &&
    typeof v['earnedSlots'] === 'number'
  );
}

function isSnapshotProfileStats(value: unknown): value is ProfileStats {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v['gamesTotal'] === 'number' &&
    typeof v['dailyCount'] === 'number' &&
    typeof v['modesEverPlayed'] === 'number' &&
    typeof v['bestByMode'] === 'object' &&
    v['bestByMode'] !== null &&
    typeof v['currentDailyStreak'] === 'number' &&
    (v['dailyRank'] === null || typeof v['dailyRank'] === 'number')
  );
}

function isSnapshotGnome(value: unknown): value is MetaSnapshotGnome {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v['color'] === 'number' &&
    typeof v['unlocked'] === 'boolean' &&
    typeof v['level'] === 'number' &&
    typeof v['prestigeCount'] === 'number'
  );
}

function isMetaSnapshot(value: unknown): value is MetaSnapshot {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    v['v'] === SCHEMA_VERSION &&
    typeof v['gold'] === 'number' &&
    typeof v['shards'] === 'number' &&
    typeof v['karat'] === 'number' &&
    Array.isArray(v['gemPool']) &&
    (v['gemPool'] as unknown[]).every((n) => typeof n === 'number') &&
    isSnapshotProgression(v['progression']) &&
    isSnapshotProfileStats(v['profileStats']) &&
    Array.isArray(v['gnomes']) &&
    (v['gnomes'] as unknown[]).every(isSnapshotGnome) &&
    typeof v['gnomeUnlocksGranted'] === 'number' &&
    (v['nextUnlockLevel'] === null || typeof v['nextUnlockLevel'] === 'number') &&
    typeof v['timestamp'] === 'number'
  );
}
